<template>
  <v-menu
    v-model="open"
    :open-on-hover="hoverMode"
    :open-delay="100"
    :close-delay="200"
    :close-on-content-click="false"
    bottom
    left
    offset-y
    :nudge-bottom="4"
    origin="top right"
    :min-width="288"
    :max-width="288"
    eager
    role="none"
    content-class="app-hub-status-card"
  >
    <template v-slot:activator="{ on }">
      <button
        type="button"
        class="hub-status"
        :class="{ 'hub-status--compact': compact }"
        :aria-label="'Verbindung: ' + connection.text"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-describedby="ids.details"
        v-on="on"
        @click="onClick"
        @blur="onBlur"
      >
        <app-icon
          v-if="compact"
          :name="connection.state === 'closed' ? 'wifiOff' : 'wifi'"
          :size="16"
          class="hub-status__wifi"
          :class="'hub-status__wifi--' + connection.state"
        />
        <span v-else class="hub-status__dot" :class="'hub-status__dot--' + connection.state" aria-hidden="true"></span>
        <span v-if="!compact" class="hub-status__text">{{ connection.text }}</span>
      </button>
      <!-- Statusmeldung neben dem Knopf: eine Live-Region im Knopf gäbe ihm keinen Namen (v-menu nimmt den Knopf als
           ersten Knoten des Slots weiter als Aktivator) -->
      <span class="app-sr-only" role="status">{{ connection.text }}</span>
    </template>

    <!-- Karte (nur Anzeige); die Zeilen beschreiben zugleich den Knopf (aria-describedby, daher eager) -->
    <div class="app-hub-status-card__body" role="group" :aria-labelledby="ids.title">
      <div class="app-hub-status-card__head">
        <app-icon
          :name="connection.state === 'closed' ? 'wifiOff' : 'wifi'"
          :size="16"
          class="app-hub-status-card__icon"
          :class="'app-hub-status-card__icon--' + connection.state"
        />
        <span :id="ids.title" class="app-hub-status-card__title">Verbindung</span>
      </div>
      <div class="app-hub-status-card__sep" aria-hidden="true"></div>
      <dl :id="ids.details" class="app-hub-status-card__rows">
        <div class="app-hub-status-card__row">
          <dt class="app-hub-status-card__label">Status</dt> <dd class="app-hub-status-card__value">
            <span class="hub-status__dot" :class="'hub-status__dot--' + connection.state" aria-hidden="true"></span>
            <span>{{ connection.text }}</span>
          </dd>
        </div>
        <div class="app-hub-status-card__row">
          <dt class="app-hub-status-card__label">Stand</dt> <dd class="app-hub-status-card__value">{{ stand }}</dd>
        </div>
        <div class="app-hub-status-card__row">
          <dt class="app-hub-status-card__label">Version</dt> <dd class="app-hub-status-card__value">{{ version }}</dd>
        </div>
        <div v-if="speed" class="app-hub-status-card__row">
          <dt class="app-hub-status-card__label">Geschwindigkeit</dt> <dd class="app-hub-status-card__value">{{ speed }}</dd>
        </div>
      </dl>
    </div>
  </v-menu>
</template>

<script>
// Verbindungsstatus der Hub-Shell (Kopfleiste rechts, links vom Benutzerknopf), im Stil der Status-Chips des Mobility
// Hub (desktop-status-bar.tsx; Analyse „topbarRight“ 3), Farben aus tokens.css.
// Chip: Knopf mit Polster 4 8, Abstand 6, Radius --r-ctl, Kante --line, --surface-raised (hell weiß), 12/16 --ink-3;
// Hover und offen --hov,
// gedrückt --press, Fokus 2 px --link mit Abstand 2; Touch mindestens 44 hoch (Polster seitlich 12). Punkt 8:
// verbunden --green-dot, getrennt --red, verbindet als Ring 1,5 px --ink-3 (Form statt nur Farbe); dazu der Text
// „Verbunden“ / „Keine Verbindung“ / „Verbinde …“ (aws/websocket: verbindung.offen/geprueft). Nur dieser Text ist
// eine Statusmeldung (role="status"), damit nicht jeder neue Stand angesagt wird. compact: runder Knopf 32 (Touch 44)
// mit WLAN-Symbol statt Punkt (16, Touch 20; verbunden --green-dot, verbindet --ink-3, getrennt durchgestrichen
// wifiOff in --red, also nicht nur über die Farbe); der Text bleibt für Screenreader.
// Karte (v-menu, wie die HoverCard im Hub: öffnet nach 100 ms, schließt 200 ms nach dem Verlassen von Chip und Karte;
// rechtsbündig unter dem Chip, Abstand 4; Breite 288, Polster 16, Radius 8, Kante --line, Schatten --sh-menu):
// Kopf WLAN-Symbol 16 in den Farben des Knopfs (verbunden --green-dot, verbindet --ink-3, getrennt wifiOff --red;
// Kundenwunsch 12.10.2026) + „Verbindung“ 14/20 500, Linie, dann Zeilen 12/16 mit Bezeichnung links (--ink-3) und Wert rechts
// (--ink 500): Status, Stand (Uhrzeit der letzten API-Antwort, $shell.stand), Version (VUE_APP_VERSION) und, wo der
// Browser sie schätzt (navigator.connection.downlink: Chrome, Edge, Android; nicht Safari/Firefox), die Geschwindigkeit
// der Internetverbindung, z. B. „≈ 4,5 Mbit/s“ (Chrome deckelt bei 10: „≥ 10 Mbit/s“); ohne Schätzung entfällt die Zeile.
// Der Mandant steht nicht in der Karte (er steht links in der Leiste; Kundenwunsch 12.10.2026).
// Bedienung: Maus über Hover; Enter/Leertaste schaltet die Karte um, Esc schließt, Verlassen des Knopfs per Tastatur
// ebenso; ein Mausklick öffnet nur (der Hover hat meist schon geöffnet). Bei Touch-Dichte öffnet und schließt ein
// Tippen, Tippen daneben schließt (v-menu ohne Hover).
import AppIcon from '../ui/AppIcon.vue';
import { verbindung } from '../../aws/websocket';
import { formatTime, LEER } from '../../utils/format';
import { notePopup } from '../../shell/shellState';

// Network Information API (nur Chromium-Browser); null, wo es sie nicht gibt
function networkInfo() {
  return typeof navigator !== 'undefined' && navigator.connection ? navigator.connection : null;
}
// „≈ 4,5 Mbit/s“ aus der Schätzung des Browsers (downlink in Mbit/s); Chrome deckelt bei 10; leer ohne Schätzung
function readSpeed() {
  const connection = networkInfo();
  const mbit = connection ? Number(connection.downlink) : 0;
  if (!(mbit > 0)) {
    return '';
  }
  if (mbit >= 10) {
    return '≥ 10 Mbit/s';
  }
  return '≈ ' + mbit.toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' Mbit/s';
}

export default {
  name: 'AppHubStatus',
  components: { AppIcon },
  props: {
    // nur der Punkt (schmale Kopfleiste); Text weiterhin für Screenreader
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      // zuletzt per Tastatur (Enter/Leertaste) geöffnet: dann schließt auch das Verlassen des Knopfs
      viaKeyboard: false,
      verbindung,
      version: process.env.VUE_APP_VERSION || LEER,
      speed: readSpeed()
    };
  },
  computed: {
    connection() {
      if (this.verbindung.geprueft === false) {
        return { state: 'pending', text: 'Verbinde …' };
      }
      if (this.verbindung.offen) {
        return { state: 'open', text: 'Verbunden' };
      }
      return { state: 'closed', text: 'Keine Verbindung' };
    },
    // „12:04 Uhr“, vor der ersten Antwort „–“
    stand() {
      return this.$shell && this.$shell.stand ? formatTime(this.$shell.stand) : LEER;
    },
    // Maus: Karte über Hover; Touch-Dichte: Tippen öffnet und schließt (v-menu mit Klick und Klick daneben)
    hoverMode() {
      return !(this.$dichte && this.$dichte.touch);
    },
    ids() {
      const base = 'app-hub-status-' + this._uid;
      return { title: base + '-titel', details: base + '-zeilen' };
    }
  },
  watch: {
    open(value) {
      this.trackPopup(value);
    }
  },
  created() {
    // nicht reaktiv: ob die Karte gerade als offenes Popup gezählt ist (hält shell.popupOffen ausgeglichen)
    this.popupNoted = false;
  },
  mounted() {
    // Schätzung des Browsers aktuell halten (ändert sich z. B. beim Wechsel ins Mobilfunknetz)
    const connection = networkInfo();
    if (connection && typeof connection.addEventListener === 'function') {
      this.onNetworkChange = () => {
        this.speed = readSpeed();
      };
      connection.addEventListener('change', this.onNetworkChange);
    }
  },
  beforeDestroy() {
    this.trackPopup(false);
    const connection = networkInfo();
    if (connection && this.onNetworkChange) {
      connection.removeEventListener('change', this.onNetworkChange);
    }
  },
  methods: {
    trackPopup(value) {
      if (value !== this.popupNoted) {
        this.popupNoted = value;
        notePopup(value);
      }
    },
    // Läuft vor dem Klick-Handler von v-menu (Touch-Dichte: v-menu schaltet dort selbst um).
    // event.detail 0 = Klick aus Enter/Leertaste
    onClick(event) {
      this.viaKeyboard = !event || event.detail === 0;
      if (!this.hoverMode) {
        return;
      }
      this.open = this.viaKeyboard ? !this.open : true;
    },
    onBlur() {
      if (this.open && this.viaKeyboard) {
        this.open = false;
      }
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------- Chip */
.hub-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: none;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 4px 8px;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
  color: var(--ink-3);
  font-family: var(--font);
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .hub-status:hover,
.hub-status[aria-expanded="true"] {
  background: var(--hov);
}
.hub-status:active {
  background: var(--press);
}
.hub-status:focus {
  outline: none;
}
.hub-status:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
html.dichte-touch .hub-status {
  min-height: 44px;
  padding: 4px 12px;
}
.hub-status__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* nur der Punkt: runder Knopf 32 (Touch 44), passend zur runden Suchpille daneben (Kundenwunsch 12.10.2026) */
.hub-status.hub-status--compact,
html.dichte-touch .hub-status.hub-status--compact {
  width: var(--h-ib);
  height: var(--h-ib);
  min-height: 0;
  padding: 0;
  border-radius: 50%;
}

/* WLAN-Symbol im kompakten Knopf (Kundenwunsch 12.10.2026) */
.hub-status__wifi {
  flex: none;
  width: var(--i-ib);
  height: var(--i-ib);
  color: var(--ink-3);
}
.hub-status__wifi--open {
  color: var(--green-dot);
}
.hub-status__wifi--closed {
  color: var(--red);
}

/* Punkt 8 (auch in der Karte) */
.hub-status__dot {
  flex: none;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
  background: var(--ink-4);
}
.hub-status__dot--pending {
  background: transparent;
  box-shadow: inset 0 0 0 1.5px var(--ink-3);
}
.hub-status__dot--open {
  background: var(--green-dot);
}
.hub-status__dot--closed {
  background: var(--red);
}

/* ---------------------------------------------------------------- Karte (hängt am [data-app]; v-menu gibt der Fläche
   das Scope-Attribut dieser Komponente mit) */
.v-menu__content.app-hub-status-card {
  box-sizing: border-box;
  padding: 16px;
  background: var(--surface-raised);
  border: 1px solid var(--line);
  border-radius: 8px;
  box-shadow: var(--sh-menu);
  color: var(--ink);
  font-family: var(--font);
}
.app-hub-status-card__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.app-hub-status-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.app-hub-status-card__icon {
  flex: none;
  color: var(--ink-3);
}
.app-hub-status-card__icon--open {
  color: var(--green-dot);
}
.app-hub-status-card__icon--closed {
  color: var(--red);
}
.app-hub-status-card__title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ink);
}
.app-hub-status-card__sep {
  height: 1px;
  background: var(--line);
}
.app-hub-status-card__rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
}
.app-hub-status-card__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  line-height: 16px;
}
.app-hub-status-card__label {
  flex: none;
  color: var(--ink-3);
}
/* lange Werte brechen rechtsbündig um statt die Karte zu sprengen */
.app-hub-status-card__value {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
  margin: 0;
  color: var(--ink);
  font-weight: 500;
  text-align: right;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .v-menu__content.app-hub-status-card {
    transition: none !important;
  }
}
</style>
